import { Trash2 } from 'lucide-react'
import { Button, ProductPicker, type PickerValue } from '@/components/ui'
import type { CaseLine } from '../api/types'

export interface CaseLineGridProps {
  lines: CaseLine[]
  disabled?: boolean
  onLineEdit: (lineId: number, edits: Partial<CaseLine>) => void
  onLineRemove: (lineId: number) => void
}

function formatMoney(value: string | null): string {
  if (!value) return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

/**
 * Per-line review grid for the Material Disposition case detail drawer.
 * Hand-rolled table rather than the EditableGrid primitive — mirrors
 * `returns-rma/components/RmaLineGrid.tsx`'s v1 approach (few lines per
 * case makes inline inputs simpler than full grid keyboard-navigation
 * machinery). Unlike RmaLineGrid, Product is a live picker in every row
 * (not read-only text) — ops needs to both add new lines and change an
 * existing line's product, not just adjust qty/lot/disposition.
 */
export function CaseLineGrid({ lines, disabled = false, onLineEdit, onLineRemove }: CaseLineGridProps) {
  return (
    <div className="overflow-x-auto rounded-sm border border-border-subtle bg-surface">
      <table className="min-w-full text-sm">
        <thead className="bg-surface-sunk text-role-table-header">
          <tr>
            <th className="px-2 py-2 text-left">#</th>
            <th className="px-2 py-2 text-left">Product</th>
            <th className="px-2 py-2 text-left">Lot #</th>
            <th className="px-2 py-2 text-right">Qty</th>
            <th className="px-2 py-2 text-left">UoM</th>
            <th className="px-2 py-2 text-right">Unit cost</th>
            <th className="px-2 py-2 text-right">Est. value</th>
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const productValue: PickerValue = line.product_id
              ? [line.product_id, line.product_name ?? '']
              : null
            return (
              <tr key={line.id} className="border-t border-border-subtle text-role-table-cell">
                <td className="px-2 py-2 text-tertiary">{line.line_no}</td>
                <td className="px-2 py-2">
                  <ProductPicker
                    value={productValue}
                    onChange={(next) =>
                      onLineEdit(line.id, {
                        product_id: next ? next[0] : null,
                        product_name: next ? next[1] : null,
                      })
                    }
                    width={200}
                    disabled={disabled}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    defaultValue={line.lot_number ?? ''}
                    disabled={disabled}
                    onBlur={(e) => {
                      if (e.target.value !== (line.lot_number ?? '')) {
                        onLineEdit(line.id, { lot_number: e.target.value })
                      }
                    }}
                    className="w-24 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                    aria-label={`Lot number for line ${line.line_no}`}
                  />
                </td>
                <td className="px-2 py-2 text-right">
                  <input
                    type="number"
                    step="any"
                    defaultValue={line.quantity ?? ''}
                    disabled={disabled}
                    onBlur={(e) => {
                      const v = e.target.value
                      if (v !== (line.quantity ?? '')) {
                        onLineEdit(line.id, { quantity: v === '' ? null : v })
                      }
                    }}
                    className="w-20 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-right text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                    aria-label={`Quantity for line ${line.line_no}`}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    defaultValue={line.uom ?? ''}
                    disabled={disabled}
                    onBlur={(e) => {
                      if (e.target.value !== (line.uom ?? '')) {
                        onLineEdit(line.id, { uom: e.target.value })
                      }
                    }}
                    className="w-16 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                    aria-label={`Unit of measure for line ${line.line_no}`}
                  />
                </td>
                <td className="px-2 py-2 text-right">
                  <input
                    type="number"
                    step="any"
                    defaultValue={line.unit_cost ?? ''}
                    disabled={disabled}
                    onBlur={(e) => {
                      const v = e.target.value
                      if (v !== (line.unit_cost ?? '')) {
                        onLineEdit(line.id, { unit_cost: v === '' ? null : v })
                      }
                    }}
                    className="w-24 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-right text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                    aria-label={`Unit cost for line ${line.line_no}`}
                  />
                </td>
                <td className="px-2 py-2 text-right text-secondary">
                  {formatMoney(line.estimated_value)}
                </td>
                <td className="px-2 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={disabled}
                    aria-label={`Remove line ${line.line_no}`}
                    onClick={() => onLineRemove(line.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
